import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Shared static label for the timesheet grid's customer/service columns.
 * Renders as a borderless button so clicks toggle the parent's editing
 * state without mounting an autocomplete on every row. Shows a chevron
 * after the name on hover/focus to advertise editability without making
 * every row look like a live dropdown; the icon matches the trigger it
 * opens into (see `Combobox`'s `ChevronDown` in combobox.tsx).
 *
 * `title` (optional) supplies the native browser tooltip — useful when
 * the visible content is `truncate`-d so the user can hover to see the
 * full name without clicking into edit mode.
 *
 * `wrap` (optional) lets the children flow onto multiple lines instead of
 * truncating. Used by the product cell so a trailing "Out of scope" badge
 * wraps below a long product name instead of squeezing it.
 *
 * `readOnly` (optional) renders the same label content as a non-interactive
 * span — no click handler, no chevron. Used for rows whose entries are
 * submitted/approved (and so immutable): the customer/activity stay visible
 * but can't be re-opened for editing. Layout/height are identical to the
 * editable button so a locked row doesn't shift relative to editable ones.
 */
export function TimesheetLabelButton({
  onClick,
  ariaLabel,
  title,
  wrap,
  readOnly,
  children,
}: {
  onClick: () => void;
  ariaLabel: string;
  title?: string;
  wrap?: boolean;
  readOnly?: boolean;
  children: ReactNode;
}) {
  if (readOnly) {
    return (
      // Mirrors the button's box (min-h-9, padding, gap) minus the
      // interactive affordances so a locked label lines up with editable
      // siblings without the hover background or chevron.
      <div
        title={title}
        className="inline-flex min-h-9 w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left"
      >
        <span
          className={wrap ? 'min-w-0 flex-1 whitespace-normal break-words' : 'min-w-0 truncate'}
        >
          {children}
        </span>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      title={title}
      // min-h-9 matches CustomerAutocomplete / ProductAutocomplete's height
      // (h-9 from the new Combobox). When the singleton-mount swaps the
      // static label for the live autocomplete on click, the row stays the
      // same height instead of growing 4px and shoving every row below it.
      className="group/label inline-flex min-h-9 w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none"
    >
      <span className={wrap ? 'min-w-0 flex-1 whitespace-normal break-words' : 'min-w-0 truncate'}>
        {children}
      </span>
      <ChevronDown
        aria-hidden
        className="shrink-0 text-muted-foreground/60 opacity-0 transition-opacity group-hover/label:opacity-100 group-focus-visible/label:opacity-100"
      />
    </button>
  );
}
